import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Accordéon simple : en-tête cliquable + contenu slotté.
 * Émet `cof2-accordion-toggle` (detail: { open }).
 */
@customElement('cof2-accordion')
export class Cof2Accordion extends LitElement {
  @property({ type: String }) label = '';
  @property({ type: Boolean, reflect: true }) open = false;
  @state() private uid = `acc-${Math.random().toString(36).slice(2, 8)}`;

  static styles = css`
    :host {
      display: block;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: var(--color-bg-elev-1);
    }
    button {
      all: unset;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: var(--space-3);
      font-weight: 600;
      cursor: pointer;
      color: var(--color-fg);
    }
    button:focus-visible {
      outline: 2px solid var(--color-accent);
      outline-offset: -2px;
    }
    .caret {
      transition: transform var(--dur-base) var(--ease-out);
      color: var(--color-fg-muted);
    }
    :host([open]) .caret {
      transform: rotate(90deg);
      color: var(--color-accent);
    }
    .panel {
      padding: 0 var(--space-3) var(--space-3);
      color: var(--color-fg);
    }
  `;

  private toggle(): void {
    this.open = !this.open;
    this.dispatchEvent(
      new CustomEvent('cof2-accordion-toggle', { detail: { open: this.open }, bubbles: true }),
    );
  }

  render() {
    const headerId = `${this.uid}-h`;
    const panelId = `${this.uid}-p`;
    return html`
      <button
        id=${headerId}
        aria-expanded=${this.open}
        aria-controls=${panelId}
        @click=${this.toggle}
      >
        <span>${this.label}</span>
        <span class="caret" aria-hidden="true">▸</span>
      </button>
      ${
        this.open
          ? html`<div id=${panelId} role="region" aria-labelledby=${headerId} class="panel">
            <slot></slot>
          </div>`
          : null
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cof2-accordion': Cof2Accordion;
  }
}
